import { Button } from "antd";
import classNames from "classnames";
import { capitalize, random } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo } from "react";

import Spinner from "@/components/Spinner";
import CardUserProfile from "@/components/v2/CardUserProfile/index";
import ErrorEmptyV2 from "@/components/v2/ErrorEmpty";
import { Event } from "@/graphql/graphql";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useListEvents } from "@/hooks/useEvents";
import useAuthStore from "@/store/useAuthStore";
import { PAGE_URL } from "@/utils/constants";

type RecommendedEventWidgetProps = {
  wrapperClassName?: string;
};

export default function RecommendedEventWidget({
  wrapperClassName = "",
}: RecommendedEventWidgetProps) {
  const { data, isLoading } = useListEvents("startTime", false);
  const router = useRouter();
  const authUser = useAuthStore.useUser();

  const dataRecommedEvents: Event[] = useMemo(() => {
    return (
      data?.items?.filter((item: any) => {
        const hasJoined = item.attendee?.items?.some(
          (row: any) =>
            row.eventAttendeeUserId === authUser?.profile?.user_id &&
            row.status === "APPROVED"
        );

        const eventDate = new Date(item.endTime);
        const isExpired = eventDate < new Date();
        return !hasJoined && !isExpired && !item?.isHidden;
      }) || []
    );
  }, [data, authUser?.profile?.user_id]);

  const randomIndex = useMemo(() => {
    if (dataRecommedEvents.length > 0) {
      return random(0, dataRecommedEvents.length - 1);
    }
    return 0;
  }, [dataRecommedEvents]);

  const recommendedEvent = dataRecommedEvents?.[randomIndex];

  const { track } = useAnalytics();

  const onClickViewMore = () => {
    track("View more recommended event", "Click", `Page ${router.pathname}`);
    router.push({
      pathname: PAGE_URL.mycalendar,
      query: { type: "community-calendar" },
    });
  };

  return (
    <div
      className={`bg-card relative px-4 py-8 w-full flex flex-col h-full justify-center ${wrapperClassName}`}
    >
      {!isLoading && recommendedEvent && (
        <div className="top-4 right-6 flex justify-end absolute z-10">
          <Button type="text" style={{ padding: 0 }} onClick={onClickViewMore}>
            <span className="text-yellow">View More Events</span>
          </Button>
        </div>
      )}
      <div
        className={classNames(
          "flex justify-center items-center text-white lg:h-[136px]",
          !recommendedEvent?.id ? "mt-0" : "mt-7 xl:mt-0"
        )}
      >
        {isLoading ? (
          <Spinner size="md" />
        ) : recommendedEvent ? (
          <Link
            href={`${PAGE_URL.events}/${recommendedEvent.id}`}
            shallow
            onClick={() => {
              track(
                "Click recommended event",
                "Click",
                `Event ID: ${recommendedEvent.id}`
              );
            }}
          >
            <CardUserProfile
              avatarSize="sm"
              variant="vertical"
              containerClassName="!pt-0"
              item={{
                firstName: recommendedEvent.name || "",
                lastName: "",
                avatarUrl: recommendedEvent.imageUrl || "",
                ...(recommendedEvent || {}) as any,
              }}
              reference="recommended-event-widget"
              findCGCTX="EVENT"
              customType={`${capitalize(recommendedEvent?.privacy)} Event`}
              disableHref
              hideMutual
              hideJobType
              squareAvatar
              type="event"
              isNameCapitalized={false}
            />
          </Link>
        ) : (
          <ErrorEmptyV2 isFullWidth which="dashboard-recommendedEvent" />
        )}
      </div>
    </div>
  );
}
